<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="document-viewer-backdrop"
      role="presentation"
      @click.self="emitClose"
    >
      <section
        class="document-viewer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-viewer-title"
      >
        <header class="document-viewer-header no-print">
          <div>
            <h5
              id="document-viewer-title"
              class="mb-1"
            >
              {{ bankMode ? 'Boleto' : 'Resumo da Cobrança' }}
            </h5>

            <small class="text-muted">
              Visualização, impressão e documentos da cobrança.
            </small>
          </div>

          <button
            type="button"
            class="btn-close"
            aria-label="Fechar"
            :disabled="gerandoPdf"
            @click="emitClose"
          ></button>
        </header>

        <div class="document-viewer-toolbar no-print">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="gerandoPdf || loadingCompany || !!companyError || documentoSemProva || (empresaPublica && !barcodeDataUrl)"
            @click="imprimirDocumento"
          >
            <i class="bi bi-printer me-1"></i>
            Imprimir
          </button>

          <button
            type="button"
            class="btn btn-outline-danger btn-sm"
            :disabled="gerandoPdf || loadingCompany || !!companyError || documentoSemProva || (empresaPublica && !barcodeDataUrl)"
            @click="baixarDemonstrativoPdf"
          >
            <span
              v-if="gerandoPdf"
              class="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>

            <i
              v-else
              class="bi bi-file-earmark-pdf me-1"
            ></i>

            {{ gerandoPdf ? 'Gerando PDF...' : textoBotaoPdfLocal }}
          </button>

          <button
            v-if="urlPdfSegura && !ambienteMock"
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="abrirUrl(urlPdfSegura)"
          >
            <i class="bi bi-box-arrow-up-right me-1"></i>
            Abrir PDF do provedor
          </button>

          <button
            v-if="urlBoletoSegura && !ambienteMock"
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="abrirUrl(urlBoletoSegura)"
          >
            <i class="bi bi-box-arrow-up-right me-1"></i>
            Abrir boleto do provedor
          </button>
        </div>

        <p v-if="empresaPublica && erroCodigoBarras" class="company-load-message no-print text-danger" role="alert">{{ erroCodigoBarras }}</p>
        <p v-if="loadingCompany" class="company-load-message no-print" role="status">Carregando identidade da empresa...</p>
        <p v-else-if="companyError" class="company-load-message no-print text-danger" role="alert">{{ companyError }}</p>
        <p v-if="documentoSemProva" class="alert alert-warning" role="alert">Identidade documental do beneficiário não comprovada. O boleto local está indisponível; consulta e cancelamento dependem separadamente da identidade bancária original.</p>
        <component :is="'style'" v-if="bankMode">{{ bankSlipPrintCss }}</component>
        <div class="document-viewer-body">
          <DocumentoBoletoBancario v-if="bankMode && !documentoSemProva" :document="bankDocument" :qr-code="qrCodeDataUrl" :barcode="barcodeDataUrl">
            <template #logo>
              <img v-if="companyLogoUrl && !logoFailed" ref="companyLogo" :src="companyLogoUrl"
                alt="Logo da escola" crossorigin="anonymous" @error="logoFailed = true" />
            </template>
          </DocumentoBoletoBancario>
          <article v-else
            id="cobranca-printable-document"
            class="charge-document"
          >
            <div
              v-if="cobrancaCancelada"
              class="document-alert document-alert-danger"
            >
              COBRANÇA CANCELADA — NÃO PAGAR
            </div>

            <div
              v-else-if="ambienteMock"
              class="document-alert document-alert-warning"
            >
              AMBIENTE MOCK — DOCUMENTO SEM VALOR BANCÁRIO
            </div>

            <div class="charge-document-heading">
              <div class="company-heading">
                <img v-if="companyLogoUrl && !logoFailed" ref="companyLogo" :src="companyLogoUrl"
                  class="company-logo" alt="Logo da empresa" crossorigin="anonymous" @error="logoFailed = true" />
                <div class="company-identity">
                  <h2 v-if="identity.primaryName">{{ identity.primaryName }}</h2>
                  <p v-if="identity.legalName" class="company-legal-name">{{ identity.legalName }}</p>
                  <p v-if="identity.cnpj">CNPJ {{ identity.cnpj }}</p>
                  <p v-for="line in identity.addressLines" :key="line">{{ line }}</p>
                  <p v-if="identity.contacts.length">{{ identity.contacts.join(' · ') }}</p>
                </div>
              </div>
              <div class="charge-document-status">
                <span class="charge-document-kicker">Resumo da Cobrança</span>
                <p>Cobrança #{{ cobranca?.id || '—' }}</p>
                <p v-if="cobranca?.numero_contrato">Contrato {{ cobranca.numero_contrato }}</p>
                <strong>{{ statusLabel }}</strong>
              </div>
            </div>

            <div class="charge-summary-grid">
              <div class="charge-summary-item">
                <span>Aluno</span>
                <strong>{{ cobranca?.aluno_nome || '—' }}</strong>
              </div>

              <div class="charge-summary-item">
                <span>Pagador</span>
                <strong>{{ cobranca?.pagador_nome || '—' }}</strong>
              </div>

              <div class="charge-summary-item">
                <span>Valor</span>
                <strong class="charge-value">
                  {{ formatMoney(cobranca?.valor_atual) }}
                </strong>
              </div>

              <div class="charge-summary-item">
                <span>Vencimento</span>
                <strong>{{ formatDate(cobranca?.data_vencimento_atual) }}</strong>
              </div>

              <div class="charge-summary-item">
                <span>Nosso número</span>
                <strong class="mono">{{ boleto?.nosso_numero || '—' }}</strong>
              </div>

              <div class="charge-summary-item">
                <span>Seu número</span>
                <strong class="mono">{{ boleto?.seu_numero || '—' }}</strong>
              </div>
            </div>

            <section class="charge-code-section">
              <div class="charge-section-title-row">
                <h3>Linha digitável</h3>

                <button
                  v-if="boleto?.linha_digitavel"
                  type="button"
                  class="btn btn-outline-secondary btn-sm no-print"
                  @click="copiar(boleto.linha_digitavel, 'Linha digitável')"
                >
                  <i class="bi bi-copy me-1"></i>
                  Copiar
                </button>
              </div>

              <code class="charge-code-block">
                {{ boleto?.linha_digitavel || 'Não disponível.' }}
              </code>

              <div
                v-if="boleto?.codigo_barras"
                class="barcode-reference"
              >
                <span>Código de barras</span>

                <div class="barcode-graphic-box">
                  <img
                    v-if="barcodeDataUrl"
                    :src="barcodeDataUrl"
                    class="barcode-image"
                    alt="Código de barras do boleto"
                  />

                  <div
                    v-else-if="gerandoCodigoBarras"
                    class="barcode-placeholder"
                  >
                    <span class="spinner-border spinner-border-sm"></span>
                    Gerando código de barras...
                  </div>

                  <div
                    v-else
                    class="barcode-placeholder text-danger"
                  >
                    {{ erroCodigoBarras || 'Código de barras gráfico indisponível.' }}
                  </div>
                </div>

                <code>{{ codigoBarrasNumerico || boleto.codigo_barras }}</code>

                <small
                  v-if="!codigoBarrasValido"
                  class="barcode-validation-warning"
                >
                  O código recebido não possui os 44 dígitos exigidos para a representação gráfica do boleto.
                </small>
              </div>
            </section>

            <section
              v-if="boleto?.pix_copia_cola"
              ref="pixSection"
              class="charge-pix-section"
            >
              <div class="charge-section-title-row">
                <div>
                  <h3>PIX</h3>
                  <p>Escaneie o QR Code ou copie o código.</p>
                </div>

                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm no-print"
                  @click="copiar(boleto.pix_copia_cola, 'PIX copia e cola')"
                >
                  <i class="bi bi-copy me-1"></i>
                  Copiar PIX
                </button>
              </div>

              <div class="pix-layout">
                <div class="pix-qrcode-box">
                  <img
                    v-if="qrCodeDataUrl"
                    :src="qrCodeDataUrl"
                    alt="QR Code PIX"
                  />

                  <div
                    v-else-if="gerandoQrCode"
                    class="pix-qrcode-placeholder"
                  >
                    <span class="spinner-border spinner-border-sm"></span>
                    Gerando QR Code...
                  </div>

                  <div
                    v-else
                    class="pix-qrcode-placeholder text-danger"
                  >
                    {{ erroQrCode || 'QR Code indisponível.' }}
                  </div>
                </div>

                <div class="pix-code-content">
                  <span>PIX copia e cola</span>
                  <code>{{ boleto.pix_copia_cola }}</code>

                </div>
              </div>
            </section>

            <section v-if="beneficiary" class="company-beneficiary">
              <span class="charge-document-kicker">Beneficiário</span>
              <strong>{{ beneficiary.nome }}</strong>
              <p>CPF/CNPJ {{ beneficiary.documento }}</p>
            </section>
            <footer class="charge-document-footer">
              <p v-if="cobrancaCancelada">
                Este documento é mantido somente para histórico. A cobrança foi cancelada.
              </p>

              <p v-else-if="ambienteMock">
                Documento gerado exclusivamente para homologação do fluxo. Não efetue pagamento.
              </p>

              <p v-else>
                Este resumo não substitui o boleto ou PDF oficial fornecido pelo provedor bancário.
              </p>

              <small>
                Documento gerado pelo EQUESTRO · {{ generatedAt }}
              </small>
            </footer>
          </article>
        </div>

        <footer class="document-viewer-footer no-print">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="gerandoPdf"
            @click="emitClose"
          >
            Fechar
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import {
  computed,
  nextTick,
  ref,
  watch
} from 'vue';

import DocumentoBoletoBancario from './DocumentoBoletoBancario.vue';
import { bankSlipDocument, bankSlipPrintCss } from '@/utils/bankSlipDocument';
import { renderBankSlipPdf } from '@/utils/bankSlipPdf';
import QRCode from 'qrcode';
import bwipjs from '@bwip-js/browser';
import { jsPDF } from 'jspdf';
import { getEmpresa } from '@/services/api/empresa';
import axios from '@/services/axios';
import { companyIdentity } from '@/utils/cobrancaCompanyIdentity';
import { imageToPdfPng } from '@/utils/pdfImages';
import { renderCobrancaPdf } from '@/utils/cobrancaDocumentPdf';

const props = defineProps({
  empresaPublica: { type: Object, default: null },
  avisoDocumento: { type: String, default: '' },
  visible: {
    type: Boolean,
    default: false
  },

  cobranca: {
    type: Object,
    default: null
  },

  boleto: {
    type: Object,
    default: null
  },

  initialMode: {
    type: String,
    default: 'resumo'
  }
});

const emit = defineEmits([
  'close',
  'copy'
]);

const gerandoQrCode = ref(false);
const gerandoCodigoBarras = ref(false);
const gerandoPdf = ref(false);
const qrCodeDataUrl = ref('');
const barcodeDataUrl = ref('');
const erroQrCode = ref('');
const erroCodigoBarras = ref('');
const pixSection = ref(null);
const empresa = ref({});
const loadingCompany = ref(false);
const companyError = ref('');
const companyLogo = ref(null);
const logoFailed = ref(false);
const companyLogoUrl = ref('');
const identity = computed(() => companyIdentity(empresa.value));
const documentoSemProva = computed(() => props.cobranca?.provedor === 'SICREDI' && !props.cobranca?.beneficiario_documento);
const beneficiary = computed(() => props.cobranca?.beneficiario_documento);
const bankMode = computed(() => props.initialMode === 'boleto');
const bankDocument = computed(() => {
  const result = bankSlipDocument(props.cobranca || {}, props.boleto || {}, identity.value, formatMoney, formatDate);
  if (props.avisoDocumento) result.warning = props.avisoDocumento;
  return result;
});
let companyRequest = 0;

async function carregarEmpresa() {
  const request = ++companyRequest;
  empresa.value = {};
  companyLogoUrl.value = '';
  logoFailed.value = false;
  companyError.value = '';
  loadingCompany.value = true;
  try {
    if (props.empresaPublica) {
      empresa.value = props.empresaPublica;
      companyLogoUrl.value = props.empresaPublica.logo_data_url || '';
      return;
    }
    const { data } = await getEmpresa();
    if (request !== companyRequest) return;
    empresa.value = data || {};
    if (identity.value.hasLogo) {
      const base = (axios.defaults.baseURL || '').replace(/\/+$/, '');
      companyLogoUrl.value = `${base}/configuracoes/empresa/logomarca?v=${encodeURIComponent(data.atualizado_em || Date.now())}`;
    }
  } catch {
    if (request === companyRequest) companyError.value = 'Não foi possível carregar a identidade da empresa. Feche e reabra o documento para tentar novamente.';
  } finally {
    if (request === companyRequest) loadingCompany.value = false;
  }
}

async function logoParaPdf() {
  await nextTick();
  const image = companyLogo.value;
  if (!image || logoFailed.value) return null;
  try { await image.decode(); } catch { return null; }
  return imageToPdfPng(image);
}

const cobrancaCancelada = computed(
  () =>
    props.cobranca?.status ===
    'CANCELADA'
);

const ambienteMock = computed(
  () =>
    [
      props.cobranca?.ambiente,
      props.cobranca?.adaptador,
      props.cobranca?.provedor
    ].some(
      (value) =>
        String(value || '')
          .trim()
          .toUpperCase() ===
        'MOCK'
    )
);

const statusLabel = computed(
  () => {
    const labels = {
      RASCUNHO: 'Rascunho',
      REGISTRO_PENDENTE: 'Registro pendente',
      REGISTRADA: 'Registrada',
      BAIXA_PENDENTE: 'Baixa pendente',
      BAIXADA: 'Baixada',
      LIQUIDADA: 'Liquidada',
      CREDITADA: 'Creditada',
      CANCELADA: 'Cancelada',
      ERRO: 'Erro'
    };

    return (
      labels[
        props.cobranca?.status
      ] ||
      props.cobranca?.status ||
      '—'
    );
  }
);

const urlPdfSegura = computed(
  () => safeHttpUrl(
    props.boleto?.url_pdf
  )
);

const urlBoletoSegura = computed(
  () => safeHttpUrl(
    props.boleto?.url_boleto
  )
);

const textoBotaoPdfLocal = computed(
  () =>
    bankMode.value ? 'Baixar boleto PDF' : ambienteMock.value
      ? 'Baixar demonstrativo PDF'
      : 'Baixar resumo PDF'
);


const codigoBarrasNumerico = computed(
  () =>
    String(
      props.boleto?.codigo_barras ||
      ''
    )
);

const codigoBarrasValido = computed(
  () =>
    /^\d{44}$/.test(
      codigoBarrasNumerico.value
    )
);

const generatedAt = computed(
  () =>
    new Intl.DateTimeFormat(
      'pt-BR',
      {
        dateStyle: 'short',
        timeStyle: 'short'
      }
    ).format(
      new Date()
    )
);

function emitClose() {
  if (gerandoPdf.value) {
    return;
  }

  emit('close');
}

function safeHttpUrl(value) {
  if (!value) {
    return null;
  }

  try {
    const parsed = new URL(
      value,
      window.location.origin
    );

    return [
      'http:',
      'https:'
    ].includes(parsed.protocol)
      ? parsed.href
      : null;
  } catch {
    return null;
  }
}

function abrirUrl(value) {
  const safeUrl = safeHttpUrl(value);

  if (!safeUrl) {
    return;
  }

  window.open(
    safeUrl,
    '_blank',
    'noopener,noreferrer'
  );
}

function formatMoney(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '—';
  }

  return new Intl.NumberFormat(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  ).format(number);
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  const raw = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);

  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat(
    'pt-BR',
    {
      timeZone: 'UTC'
    }
  ).format(date);
}

function copiar(value, label) {
  const text = String(value || '');

  if (!text) {
    return;
  }

  emit('copy', {
    value: text,
    label
  });
}

async function gerarCodigoBarras() {
  barcodeDataUrl.value = '';
  erroCodigoBarras.value = '';

  if (!codigoBarrasNumerico.value) {
    return;
  }

  if (!codigoBarrasValido.value) {
    erroCodigoBarras.value =
      'O código de barras deve conter exatamente 44 dígitos.';
    return;
  }

  gerandoCodigoBarras.value = true;

  try {
    const canvas =
      document.createElement('canvas');

    bwipjs.toCanvas(
      canvas,
      {
        bcid: 'interleaved2of5',
        text: codigoBarrasNumerico.value,
        scale: 3,
        height: 13,
        includetext: false,
        paddingwidth: 0,
        paddingheight: 0,
        backgroundcolor: 'FFFFFF'
      }
    );

    barcodeDataUrl.value =
      canvas.toDataURL('image/png');
  } catch (error) {
    console.error(
      'Erro ao gerar código de barras do boleto:',
      error
    );

    erroCodigoBarras.value =
      'Não foi possível gerar o código de barras gráfico.';
  } finally {
    gerandoCodigoBarras.value = false;
  }
}

async function gerarQrCode() {
  const payload = String(
    props.boleto?.pix_copia_cola || ''
  );

  qrCodeDataUrl.value = '';
  erroQrCode.value = '';

  if (!payload) {
    return;
  }

  gerandoQrCode.value = true;

  try {
    qrCodeDataUrl.value =
      await QRCode.toDataURL(
        payload,
        {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 360,
          type: 'image/png'
        }
      );
  } catch (error) {
    console.error(
      'Erro ao gerar QR Code PIX:',
      error
    );

    erroQrCode.value =
      'Não foi possível gerar o QR Code.';
  } finally {
    gerandoQrCode.value = false;
  }
}

function htmlEscape(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

async function imprimirDocumento() {
  if (loadingCompany.value || companyError.value || documentoSemProva.value) return;
  const printable = document.getElementById(
    props.initialMode === 'boleto' ? 'bank-slip-printable' : 'cobranca-printable-document'
  );

  if (!printable) {
    return;
  }

  const printWindow = window.open(
    '',
    '_blank',
    'width=960,height=760'
  );

  if (!printWindow) {
    window.alert(
      'O navegador bloqueou a janela de impressão.'
    );

    return;
  }

  printWindow.opener = null;
  await Promise.all([gerarCodigoBarras(), gerarQrCode(), logoParaPdf()]);
  await nextTick();

  printWindow.document.open();
  printWindow.document.write(`<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${htmlEscape(`Cobrança #${props.cobranca?.id || ''}`)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 24px; color: #1f2933; font-family: Arial, sans-serif; }
    button { display: none !important; }
    .charge-document { width: 100%; max-width: 820px; margin: 0 auto; border: 1px solid #cbd5e1; border-radius: 8px; padding: 24px; }
    .document-alert { padding: 12px; margin-bottom: 18px; border: 2px solid; border-radius: 6px; font-weight: 700; text-align: center; }
    .document-alert-danger { border-color: #b91c1c; color: #991b1b; background: #fef2f2; }
    .document-alert-warning { border-color: #b45309; color: #92400e; background: #fffbeb; }
    .charge-document-heading { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #334155; padding-bottom: 16px; }
    .charge-document-kicker, .charge-summary-item span, .barcode-reference span, .pix-code-content span { display: block; color: #64748b; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    h2 { margin: 6px 0; font-size: 24px; }
    h3 { margin: 0 0 10px; font-size: 16px; }
    p { margin: 4px 0; }
    .charge-document-status { min-width: 130px; text-align: right; }
    .charge-document-status span { display: block; color: #64748b; font-size: 11px; text-transform: uppercase; }
    .charge-summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin: 20px 0; }
    .charge-summary-item { border: 1px solid #dbe3ea; border-radius: 6px; padding: 12px; }
    .charge-summary-item strong { display: block; margin-top: 5px; }
    .charge-value { color: #166534; font-size: 20px; }
    .charge-code-section, .charge-pix-section { border-top: 1px solid #cbd5e1; padding-top: 18px; margin-top: 18px; }
    .charge-section-title-row { display: flex; justify-content: space-between; gap: 16px; }
    .charge-code-block, .pix-code-content code { display: block; padding: 12px; border: 1px solid #dbe3ea; border-radius: 6px; background: #f8fafc; overflow-wrap: anywhere; font-family: monospace; }
    .barcode-reference { margin-top: 12px; }
    .barcode-graphic-box { display: flex; align-items: center; justify-content: center; min-height: 82px; margin-top: 8px; padding: 8px 10px; border: 1px solid #dbe3ea; background: #fff; overflow: hidden; }
    .barcode-image { display: block; width: 100%; max-width: 720px; height: auto; max-height: 82px; object-fit: contain; }
    .barcode-placeholder { color: #64748b; text-align: center; }
    .barcode-reference code { display: block; margin-top: 7px; overflow-wrap: anywhere; text-align: center; letter-spacing: 0.08em; }
    .pix-layout { display: grid; grid-template-columns: 220px 1fr; gap: 22px; align-items: center; }
    .pix-qrcode-box img { display: block; width: 210px; height: 210px; object-fit: contain; }
    .pix-code-content small { display: block; margin-top: 10px; }
    .charge-document-footer { margin-top: 24px; padding-top: 16px; border-top: 1px solid #cbd5e1; color: #475569; }
    .mono { font-family: monospace; }
    .company-heading { display: flex; gap: 14px; flex: 1; min-width: 0; }
    .company-logo { width: auto; max-width: 100px; max-height: 70px; object-fit: contain; align-self: flex-start; }
    .company-identity { min-width: 0; overflow-wrap: anywhere; }
    .company-identity p { font-size: 10px; line-height: 1.4; }
    .company-identity h2 { font-size: 18px; }
    .charge-document-status { max-width: 190px; }
    .company-beneficiary { margin-top: 16px; padding-top: 12px; border-top: 1px solid #cbd5e1; font-size: 11px; }
    .company-beneficiary strong { display: block; margin-top: 4px; }
    .charge-summary-item { border: 0; border-bottom: 1px solid #e2e8f0; border-radius: 0; padding: 8px 0; }
    .pix-code-content { min-width: 0; }
    .charge-document-heading, .charge-code-section, .charge-pix-section, .company-beneficiary, .charge-document-footer { break-inside: avoid; }
    @page { size: A4; margin: 12mm; }
    @media print {
      body { padding: 0; font-size: 10px; }
      .charge-document { border: 0; max-width: none; padding: 0; }
      .charge-summary-grid { margin: 12px 0; gap: 8px 16px; }
      .charge-code-section, .charge-pix-section { margin-top: 12px; padding-top: 12px; }
      .pix-layout { grid-template-columns: 150px minmax(0, 1fr); gap: 16px; }
      .pix-qrcode-box img { width: 145px; height: 145px; }
      .charge-document-footer { margin-top: 12px; padding-top: 10px; }
    }
    ${props.initialMode === 'boleto' ? bankSlipPrintCss : ''}
  </style>
</head>
<body>
  ${printable.outerHTML}
  <script>
    window.addEventListener('load', function () {
      Promise.all(Array.from(document.images, function (image) {
        return image.decode().catch(function () {});
      })).then(function () { window.print(); });
    });
  <${'/'}script>
</body>
</html>`);
  printWindow.document.close();
}

async function baixarDemonstrativoPdf() {
  if (gerandoPdf.value || loadingCompany.value || companyError.value || documentoSemProva.value) {
    return;
  }

  gerandoPdf.value = true;

  try {
    if (
      codigoBarrasValido.value &&
      !barcodeDataUrl.value
    ) {
      await gerarCodigoBarras();
    }

    if (
      props.boleto?.pix_copia_cola &&
      !qrCodeDataUrl.value
    ) {
      await gerarQrCode();
    }

    if (props.empresaPublica && !barcodeDataUrl.value) throw new Error('Documento indisponível: falha no código de barras.');

    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4'
    });

    if (bankMode.value) {
      renderBankSlipPdf(doc, bankDocument.value, { barcode: barcodeDataUrl.value, qrCode: qrCodeDataUrl.value, logo: await logoParaPdf() });
    } else renderCobrancaPdf(doc, {
      identity: identity.value, logo: await logoParaPdf(),
      cobranca: props.cobranca || {}, boleto: props.boleto || {},
      statusLabel: statusLabel.value, generatedAt: generatedAt.value,
      ambienteMock: ambienteMock.value, cobrancaCancelada: cobrancaCancelada.value,
      barcode: barcodeDataUrl.value, qrCode: qrCodeDataUrl.value,
      formatMoney, formatDate
    });

    doc.save(
      `${
        bankMode.value ? 'boleto' : ambienteMock.value
          ? 'demonstrativo'
          : 'resumo'
      }-cobranca-${props.cobranca?.id || 'sem-id'}.pdf`
    );
  } catch (error) {
    console.error(
      'Erro ao gerar PDF da cobrança:',
      error
    );

    window.alert(
      'Não foi possível gerar o PDF da cobrança.'
    );
  } finally {
    gerandoPdf.value = false;
  }
}

watch(
  () => [
    props.visible,
    props.boleto?.pix_copia_cola,
    props.boleto?.codigo_barras
  ],
  async ([visible]) => {
    if (!visible) {
      qrCodeDataUrl.value = '';
      barcodeDataUrl.value = '';
      erroQrCode.value = '';
      erroCodigoBarras.value = '';
      ++companyRequest;
      empresa.value = {};
      companyLogoUrl.value = '';
      return;
    }

    await Promise.all([
      carregarEmpresa(),
      gerarQrCode(),
      gerarCodigoBarras()
    ]);

    await nextTick();

    if (
      props.initialMode === 'pix' &&
      pixSection.value
    ) {
      pixSection.value.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  },
  {
    immediate: true
  }
);
</script>

<style scoped>
.document-viewer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1090;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.65);
}

.document-viewer-modal {
  display: flex;
  flex-direction: column;
  width: min(1040px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 22px 64px rgba(15, 23, 42, 0.28);
}

.document-viewer-header,
.document-viewer-toolbar,
.document-viewer-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-color: #dbe3ea;
  background: #fff;
}

.document-viewer-header {
  justify-content: space-between;
  border-bottom: 1px solid #dbe3ea;
}

.document-viewer-toolbar {
  flex-wrap: wrap;
  border-bottom: 1px solid #dbe3ea;
  background: #f8fafc;
}

.document-viewer-footer {
  justify-content: flex-end;
  border-top: 1px solid #dbe3ea;
}

.document-viewer-body {
  overflow: auto;
  padding: 24px;
  background: #eef2f6;
}

.charge-document {
  width: min(820px, 100%);
  margin: 0 auto;
  padding: 28px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  color: #1f2933;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
}

.document-alert {
  margin-bottom: 20px;
  padding: 12px 16px;
  border: 2px solid;
  border-radius: 7px;
  font-weight: 800;
  text-align: center;
}

.document-alert-danger {
  border-color: #b91c1c;
  color: #991b1b;
  background: #fef2f2;
}

.document-alert-warning {
  border-color: #b45309;
  color: #92400e;
  background: #fffbeb;
}

.charge-document-heading {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding-bottom: 18px;
  border-bottom: 2px solid #334155;
}

.charge-document-kicker,
.charge-summary-item span,
.barcode-reference span,
.pix-code-content span {
  display: block;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.charge-document-heading h2 {
  margin: 5px 0;
  font-size: 1.5rem;
}

.charge-document-heading p {
  margin: 0;
  color: #64748b;
}

.charge-document-status {
  min-width: 130px;
  text-align: right;
}

.charge-document-status span {
  display: block;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.charge-document-status strong {
  display: block;
  margin-top: 4px;
}

.charge-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 22px 0;
}

.charge-summary-item {
  min-width: 0;
  padding: 13px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.charge-summary-item strong {
  display: block;
  margin-top: 5px;
  overflow-wrap: anywhere;
}

.charge-value {
  color: #166534;
  font-size: 1.12rem;
}

.charge-code-section,
.charge-pix-section {
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid #cbd5e1;
  scroll-margin-top: 20px;
}

.charge-section-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.charge-section-title-row h3 {
  margin: 0;
  font-size: 1rem;
}

.charge-section-title-row p {
  margin: 3px 0 0;
  color: #64748b;
  font-size: 0.85rem;
}

.charge-code-block,
.pix-code-content code {
  display: block;
  padding: 12px;
  border: 1px solid #dbe3ea;
  border-radius: 6px;
  background: #f8fafc;
  color: #1f2933;
  overflow-wrap: anywhere;
  white-space: normal;
}

.barcode-reference {
  margin-top: 12px;
}

.barcode-graphic-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 96px;
  margin-top: 8px;
  padding: 10px 12px;
  overflow: hidden;
  border: 1px solid #dbe3ea;
  border-radius: 6px;
  background: #fff;
}

.barcode-image {
  display: block;
  width: 100%;
  max-width: 760px;
  height: auto;
  max-height: 92px;
  object-fit: contain;
}

.barcode-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #64748b;
  text-align: center;
}

.barcode-reference code {
  display: block;
  margin-top: 7px;
  color: #334155;
  overflow-wrap: anywhere;
  text-align: center;
  letter-spacing: 0.08em;
}

.barcode-validation-warning {
  display: block;
  margin-top: 6px;
  color: #991b1b;
  font-size: 0.78rem;
  font-weight: 600;
}

.pix-layout {
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 24px;
  align-items: center;
}

.pix-qrcode-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 230px;
  border: 1px solid #dbe3ea;
  border-radius: 8px;
  background: #fff;
}

.pix-qrcode-box img {
  display: block;
  width: 220px;
  height: 220px;
  object-fit: contain;
}

.pix-qrcode-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #64748b;
  text-align: center;
}

.pix-code-content small {
  display: block;
  margin-top: 10px;
  color: #64748b;
  overflow-wrap: anywhere;
}

.charge-document-footer {
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid #cbd5e1;
  color: #475569;
}

.charge-document-footer p {
  margin: 0 0 5px;
}

.charge-document-footer small {
  color: #64748b;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.company-heading { display: flex; gap: 16px; flex: 1; min-width: 0; }
.company-logo { width: auto; max-width: 110px; max-height: 80px; object-fit: contain; align-self: flex-start; }
.company-identity { min-width: 0; overflow-wrap: anywhere; }
.company-identity p { font-size: 0.78rem; line-height: 1.5; }
.company-identity .company-legal-name { font-weight: 600; color: #334155; }
.charge-document-status { max-width: 200px; font-size: 0.85rem; }
.charge-document-status p { margin-top: 5px; }
.company-beneficiary { margin-top: 20px; padding-top: 16px; border-top: 1px solid #cbd5e1; font-size: 0.82rem; overflow-wrap: anywhere; }
.company-beneficiary strong { display: block; margin: 5px 0; }
.company-beneficiary p { margin: 0; }
.company-load-message { margin: 0; padding: 10px 20px; }

@media (max-width: 720px) {
  .document-viewer-backdrop {
    padding: 0;
  }

  .document-viewer-modal {
    width: 100%;
    max-height: 100vh;
    min-height: 100vh;
    border-radius: 0;
  }

  .document-viewer-body,
  .charge-document {
    padding: 16px;
  }

  .charge-document-heading,
  .charge-section-title-row {
    flex-direction: column;
  }

  .charge-document-status {
    min-width: 0;
    text-align: left;
  }

  .charge-summary-grid,
  .pix-layout {
    grid-template-columns: 1fr;
  }

  .pix-qrcode-box {
    min-height: 210px;
  }
}
</style>
